import { useState } from 'react';

import { HorizontalTabs } from '@opendoor/mosaic';

import VerticalLayout from '../components/layouts/VerticalLayout';
import VisitsList from '../components/visits/VisitsList';
import { useSessionContext } from '../contexts/SessionContext';
import { OdProtosTrippyData_VisitOrdering_Column } from '../graphql/athena/types/globalTypes';
import { SearchVisits_trippy_searchVisits_visits } from '../graphql/athena/types/SearchVisits';
import useSearchVisits from '../graphql/queries/trippy/useSearchVisits';

export enum VisitsTab {
  NEW = 'New',
  ASSIGNED = 'Assigned',
  COMPLETED = 'Completed',
}

const Visits = () => {
  const { session } = useSessionContext();
  const [selectedTab, setSelectedTab] = useState(VisitsTab.NEW);
  const [addressDebounce, setAddressDebounce] = useState('');

  const orgIds: string[] = [];
  if (session.human?.organizations) {
    session.human.organizations.forEach((o) => {
      if (o) orgIds.push(o.uuid);
    });
  }

  const { data } = useSearchVisits({
    variables: {
      assignedOrganizationIds: orgIds,
      cancelledAt: { params: { unset: true } },
      orderings: [
        { column: OdProtosTrippyData_VisitOrdering_Column.SCHEDULE_START_AT, isDescending: false },
      ],
      pagination: { first: 150 },
    },
    skip: orgIds.length === 0,
  });
  const visits = data?.trippy.searchVisits?.visits || [];
  const partitionedVisits = partitionVisits(visits);

  const tabs = [
    {
      title: `${VisitsTab.NEW} (${partitionedVisits.new.length})`,
      isActive: selectedTab === VisitsTab.NEW,
      onClick: () => {
        setSelectedTab(VisitsTab.NEW);
      },
    },
    {
      title: `${VisitsTab.ASSIGNED} (${partitionedVisits.assigned.length})`,
      isActive: selectedTab === VisitsTab.ASSIGNED,
      onClick: () => {
        setSelectedTab(VisitsTab.ASSIGNED);
      },
    },
    {
      title: `${VisitsTab.COMPLETED} (${partitionedVisits.completed.length})`,
      isActive: selectedTab === VisitsTab.COMPLETED,
      onClick: () => {
        setSelectedTab(VisitsTab.COMPLETED);
      },
    },
  ];

  return (
    <VerticalLayout style={styles.container}>
      <div data-testid="visits-address-input" style={styles.inputContainer}>
        <input
          style={styles.input}
          value={addressDebounce}
          onChange={(e) => setAddressDebounce(e.target.value)}
          placeholder={'Search an address'}
        />
      </div>
      <HorizontalTabs links={tabs} />
      {selectedTab === VisitsTab.NEW && (
        <VisitsList
          visits={partitionedVisits.new}
          selectedTab={selectedTab}
          addressDebounce={addressDebounce}
        />
      )}
      {selectedTab === VisitsTab.ASSIGNED && (
        <VisitsList
          visits={partitionedVisits.assigned}
          selectedTab={selectedTab}
          addressDebounce={addressDebounce}
        />
      )}
      {selectedTab === VisitsTab.COMPLETED && (
        <VisitsList
          visits={partitionedVisits.completed}
          selectedTab={selectedTab}
          addressDebounce={addressDebounce}
        />
      )}
    </VerticalLayout>
  );
};

const partitionVisits = (visits: SearchVisits_trippy_searchVisits_visits[]) => {
  const completedVisits: SearchVisits_trippy_searchVisits_visits[] = [];
  const assignedVisits: SearchVisits_trippy_searchVisits_visits[] = [];
  const newVisits: SearchVisits_trippy_searchVisits_visits[] = [];

  visits.forEach((v) => {
    if (!!v.rejectedAt) {
      return;
    } else if (!!v.checkOutAt) {
      completedVisits.push(v);
    } else if (!!v.assignedUserToken) {
      assignedVisits.push(v);
    } else {
      newVisits.push(v);
    }
  });

  return { new: newVisits, assigned: assignedVisits, completed: completedVisits };
};

export default Visits;

const styles = {
  container: {
    backgroundColor: 'white',
    minHeight: '95vh',
    maxHeight: '95vh',
  },
  inputContainer: {
    marginLeft: '1%',
    marginTop: '1%',
  },
  input: {
    width: '25%',
  },
};
